import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import './MyRequests.css';

const unitLogo = '/images/LP_Unit_logo.png';

function MyRequests() {
  const { user, userData, logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Fetch all requests that could belong to this user across all historical ID formats.
  // Requests may have been submitted under:
  //   1. user.uid (current Firestore UID — may be old Firebase UID for migrated users)
  //   2. userData.discordId (Discord snowflake ID — used before/after migration)
  //   3. userData.username (normalized name e.g. "J. Draco" — used as username field on requests)
  useEffect(() => {
    if (!user) return;

    const fetchAllRequests = async () => {
      try {
        setLoading(true);
        const requestsRef = collection(db, 'requests');
        const seen = new Set();
        const allDocs = [];

        // Build the list of unique IDs to query by userId
        const userIds = new Set();
        if (user.uid) userIds.add(user.uid);
        if (userData?.discordId && userData.discordId !== user.uid) userIds.add(userData.discordId);
        if (userData?.firestoreId && userData.firestoreId !== user.uid) userIds.add(userData.firestoreId);

        // Query by each userId variant
        for (const uid of userIds) {
          const q = query(requestsRef, where('userId', '==', uid));
          const snap = await getDocs(q);
          snap.docs.forEach(doc => {
            if (!seen.has(doc.id)) {
              seen.add(doc.id);
              allDocs.push({ id: doc.id, ...doc.data() });
            }
          });
        }

        // Also query by username to catch legacy records that may have a different userId
        const usernameVariants = new Set();
        if (userData?.username) usernameVariants.add(userData.username);
        if (userData?.normalizedName) usernameVariants.add(userData.normalizedName);

        for (const uname of usernameVariants) {
          const q = query(requestsRef, where('username', '==', uname));
          const snap = await getDocs(q);
          snap.docs.forEach(doc => {
            if (!seen.has(doc.id)) {
              seen.add(doc.id);
              allDocs.push({ id: doc.id, ...doc.data() });
            }
          });
        }

        // Sort by createdAt descending (handle null timestamps gracefully)
        allDocs.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });

        setRequests(allDocs);
      } catch (err) {
        console.error('Error fetching requests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllRequests();
  }, [user, userData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Unassigned': return '#ff6b6b';
      case 'In Progress': return '#ffd93d';
      case 'Ready for Config': return '#6bcb77';
      case 'Pushed to ModPack': return '#4d96ff';
      case 'Payment Received': return '#9b59b6';
      case 'Closed Out': return '#888';
      default: return '#888';
    }
  };

  return (
    <div className="my-requests-page" style={{ backgroundImage: `url(/images/coming_soon_bg.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="my-requests-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
          {user && <button className="logout-btn" onClick={logout}>Logout</button>}
        </div>
      </nav>

      <main className="my-requests-content">
        <div className="my-requests-header">
          <h2>My Request History</h2>
          <p className="user-info">Logged in as: {userData?.username || userData?.serverNickname || 'User'}</p>
        </div>

        <div className="requests-table-container">
          {loading ? (
            <div className="loading">Loading your requests...</div>
          ) : requests.length === 0 ? (
            <div className="no-requests">No requests found. If this is incorrect, please contact J. Draco or W. Chan.</div>
          ) : (
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request.id} className={request.status === 'Closed Out' ? 'row-completed' : ''}>
                    <td>{formatDate(request.createdAt)}</td>
                    <td><span className={`type-badge ${request.type?.toLowerCase().replace(/\s+/g, '-')}`}>{request.type || 'N/A'}</span></td>
                    <td><span className="status-badge" style={{ color: getStatusColor(request.status) }}>{request.status || 'Pending'}</span></td>
                    <td>
                      <button 
                        className="view-details-btn"
                        onClick={() => setSelectedRequest(request)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Request Details Modal */}
      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedRequest(null)}>×</button>
            <h2 className="modal-title">Request Details</h2>
            <div className="detail-grid">
              <div className="detail-item"><label>Date:</label><span>{formatDate(selectedRequest.createdAt)}</span></div>
              <div className="detail-item"><label>Type:</label><span>{selectedRequest.type}</span></div>
              <div className="detail-item"><label>Status:</label><span style={{ color: getStatusColor(selectedRequest.status) }}>{selectedRequest.status}</span></div>
              <div className="detail-item"><label>ZAP:</label><span>{selectedRequest.zap || 'N/A'}</span></div>
            </div>

            <div className="detail-section">
              <h3>Configuration</h3>
              <div className="config-details">
                {/* Vest & Belt Options */}
                {selectedRequest.vestType && <p><strong>Vest:</strong> {selectedRequest.vestType}</p>}
                {selectedRequest.placard && <p><strong>Placard:</strong> {selectedRequest.placard}</p>}
                {selectedRequest.chestRig && <p><strong>Chest Rig:</strong> {selectedRequest.chestRig}</p>}
                {selectedRequest.belt && <p><strong>Belt:</strong> {selectedRequest.belt}</p>}
                {selectedRequest.holster && <p><strong>Holster:</strong> {selectedRequest.holster}</p>}
                
                {/* Helmet Options */}
                {selectedRequest.helmet && <p><strong>Helmet:</strong> {selectedRequest.helmet}</p>}
                {selectedRequest.rails && <p><strong>Rails:</strong> {selectedRequest.rails}</p>}
                {selectedRequest.cover && <p><strong>Cover:</strong> {selectedRequest.cover}</p>}
                {selectedRequest.batteryPack && <p><strong>Battery Pack:</strong> {selectedRequest.batteryPack}</p>}
                {selectedRequest.strobe && <p><strong>Strobe:</strong> {selectedRequest.strobe}</p>}
                {selectedRequest.earProtection && <p><strong>Ear Protection:</strong> {selectedRequest.earProtection}</p>}
                {selectedRequest.taskLight && <p><strong>Task Light:</strong> {selectedRequest.taskLight}</p>}
                {selectedRequest.nodMount && <p><strong>NOD Mount:</strong> {selectedRequest.nodMount}</p>}
                {selectedRequest.nod && <p><strong>NOD:</strong> {selectedRequest.nod}</p>}
                
                {/* Revision fields */}
                {selectedRequest.helmetDetails && <p><strong>Helmet Revision:</strong> {selectedRequest.helmetDetails}</p>}
                {selectedRequest.vestDetails && <p><strong>Vest Revision:</strong> {selectedRequest.vestDetails}</p>}
                {selectedRequest.beltDetails && <p><strong>Belt Revision:</strong> {selectedRequest.beltDetails}</p>}
                
                {/* ZAP Change fields */}
                {selectedRequest.currentZap && <p><strong>Current ZAP:</strong> {selectedRequest.currentZap}</p>}
                {selectedRequest.updatedZap && <p><strong>Updated ZAP:</strong> {selectedRequest.updatedZap}</p>}
                {selectedRequest.colorOption && selectedRequest.type === 'ZAP' && <p><strong>Color Option:</strong> {selectedRequest.colorOption}</p>}
                
                {/* Custom items */}
                {selectedRequest.items && selectedRequest.items.length > 0 && (
                  <>
                    <p><strong>Items:</strong></p>
                    {selectedRequest.items.map((item, idx) => (
                      <p key={idx} style={{ marginLeft: '1rem' }}>
                        • {item.type}: {item.specification || item.description}
                        {item.imageLink && <span> (Ref: {item.imageLink})</span>}
                      </p>
                    ))}
                  </>
                )}
                
                {/* Add-ons */}
                {selectedRequest.premiumAsset && <p><strong>Premium Asset:</strong> Yes</p>}
                {selectedRequest.customAcex && <p><strong>Custom ACEX:</strong> Yes</p>}
                
                {/* Price */}
                {selectedRequest.totalPrice && <p><strong>Total Price:</strong> ${selectedRequest.totalPrice}</p>}
              </div>
            </div>

            {selectedRequest.comments && (
              <div className="detail-section">
                <h3>Comments</h3>
                <p className="user-notes">{selectedRequest.comments}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MyRequests;
